/*
 * THTD interactions — vanilla replacements for webflow.js / jQuery.
 * No dependencies. ES5-safe. Loaded deferred.
 *
 * Modules:
 *   A) Hamburger menu
 *   B) Size-guide popups
 *   C) Add-to-cart smileys — gather on hover
 *   D) Scroll-reveal (IntersectionObserver — .thtd-reveal)
 *   E) 3D-cursor hover — OMITTED (pink icon stays hidden, matches live original)
 *   F) Contact hover boxes (colour swap + icon grow — ground-truth from live)
 *   G) Load-fade smileys (stuck invisible after webflow removal)
 *   H) Lottie loaders — REMOVED, replaced by CSS flipbook (thtd-loader.liquid)
 *   I) Custom options readiness checklist (.cpo-checklist — must not be
 *      an inline script inside the form, udesly JSON.parses the first one)
 *   J) Form script — window.Webflow.push replacement
 *   K) FAQ accordion (sections/faq.liquid)
 */
(function () {
  'use strict';

  /* -----------------------------------------------------------------------
   * Helpers
   * --------------------------------------------------------------------- */
  var prefersReducedMotion = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function onReady(fn) {
    if (document.readyState !== 'loading') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  /* -----------------------------------------------------------------------
   * A) HAMBURGER MENU
   *    Findings from toohightodie.webflow.css.liquid:
   *    - .nav_menu at ≤991px: display:none by default; is positioned
   *      absolute, full-width, with flex-direction:column; background matches
   *      nav bar; padding-top ~20vw; inset:0% 0% auto auto.
   *    - .menu_button at ≤991px: display:flex (hamburger visible on mobile).
   *    - We toggle .is-open on .nav_menu which sets display:flex (overriding
   *      the media-query display:none via a higher-specificity inline style
   *      + transition). We also set aria-expanded on the button.
   *    - Close on any nav link click and on Escape key.
   * --------------------------------------------------------------------- */
  onReady(function () {
    var btn = document.querySelector('.menu_button');
    var nav = document.querySelector('.nav_menu');
    if (!btn || !nav) { return; }

    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('aria-controls', 'thtd-nav-menu');
    nav.id = nav.id || 'thtd-nav-menu';

    function openMenu() {
      nav.style.display = 'flex';
      // Force reflow so the transition fires
      nav.offsetHeight; // eslint-disable-line no-unused-expressions
      nav.style.opacity = '1';
      btn.setAttribute('aria-expanded', 'true');
      btn.classList.add('is-active');
    }

    function closeMenu() {
      nav.style.opacity = '0';
      btn.setAttribute('aria-expanded', 'false');
      btn.classList.remove('is-active');
      // Hide after transition
      var duration = prefersReducedMotion ? 0 : 200;
      setTimeout(function () {
        // Only hide if still closed (user didn't reopen during transition)
        if (btn.getAttribute('aria-expanded') === 'false') {
          nav.style.display = 'none';
        }
      }, duration);
    }

    function isOpen() {
      return btn.getAttribute('aria-expanded') === 'true';
    }

    btn.addEventListener('click', function (e) {
      e.preventDefault();
      if (isOpen()) {
        closeMenu();
      } else {
        openMenu();
      }
    });

    // Close on nav link click (navigating away)
    var navLinks = nav.querySelectorAll('a');
    for (var i = 0; i < navLinks.length; i++) {
      navLinks[i].addEventListener('click', function () {
        closeMenu();
      });
    }

    // Close on Escape
    document.addEventListener('keydown', function (e) {
      if ((e.key === 'Escape' || e.keyCode === 27) && isOpen()) {
        closeMenu();
        btn.focus();
      }
    });
  });

  /* -----------------------------------------------------------------------
   * B) SIZE-GUIDE POPUPS
   *    Each popup (.popup / .popup.custom) starts inline display:none;opacity:0
   *    (product-main) or without inline opacity (custom-jersey but .popup.custom
   *    is display:none in CSS). We show with flex + transition opacity.
   *    .popup_content_wrapper: inline transform scale(1.1) rotateZ(5deg) →
   *    animate to scale(1) rotateZ(0).
   *    Scoped: each size-guide link opens only the popup inside its own
   *    closest ancestor section (.shopify-section).
   * --------------------------------------------------------------------- */
  onReady(function () {
    var sizeLinks = document.querySelectorAll('.sizes_holder .paragraph_normal');

    for (var i = 0; i < sizeLinks.length; i++) {
      (function (link) {
        // Walk up to find the containing section, then find the popup in it
        var section = link.closest('.shopify-section') ||
                      link.closest('.box') ||
                      document.body;
        var popup = section.querySelector('.popup');
        if (!popup) { return; }

        var wrapper = popup.querySelector('.popup_content_wrapper');

        function openPopup(e) {
          e.preventDefault();
          popup.style.display = 'flex';
          popup.style.transition = prefersReducedMotion
            ? 'none'
            : 'opacity 300ms ease';
          popup.style.opacity = '0';
          popup.offsetHeight; // reflow
          popup.style.opacity = '1';

          if (wrapper) {
            wrapper.style.transition = prefersReducedMotion
              ? 'none'
              : 'transform 500ms cubic-bezier(0.215, 0.61, 0.355, 1)';
            // Start state already set inline (scale 1.1, rotateZ 5deg)
            wrapper.offsetHeight; // reflow
            wrapper.style.transform =
              'translate3d(0,0,0) scale3d(1,1,1) rotateZ(0deg)';
          }
        }

        function closePopup() {
          popup.style.opacity = '0';
          if (wrapper) {
            wrapper.style.transform =
              'translate3d(0,0,0) scale3d(1.1,1.1,1) rotateZ(5deg)';
          }
          var duration = prefersReducedMotion ? 0 : 300;
          setTimeout(function () {
            popup.style.display = 'none';
          }, duration);
        }

        link.addEventListener('click', openPopup);

        // Close button
        var closeBtn = popup.querySelector('.popup_close_bttn');
        if (closeBtn) {
          closeBtn.addEventListener('click', function (e) {
            e.preventDefault();
            closePopup();
          });
        }

        // Click on backdrop (popup itself, not its children)
        popup.addEventListener('click', function (e) {
          if (e.target === popup) {
            closePopup();
          }
        });

        // Escape key
        document.addEventListener('keydown', function (e) {
          if ((e.key === 'Escape' || e.keyCode === 27) &&
              popup.style.display !== 'none' &&
              popup.style.opacity !== '0') {
            closePopup();
          }
        });
      }(sizeLinks[i]));
    }
  });

  /* -----------------------------------------------------------------------
   * C) ADD-TO-CART SMILEYS — gather on HOVER (user request; was on click).
   *    5 smileys (.smiley_01..05) inside .add_button_wrapper sit at REST in
   *    SCATTERED positions (theme sets inline translate3d(Xpx,Ypx,0)
   *    scale3d(0.6,0.6,1), opacity 0). On HOVER of the add-to-cart button they
   *    GATHER to centre — translate→(0,0), scale 0.6→1, opacity 0→1 with a
   *    spring (~450ms). On mouse-leave they SCATTER back out + fade. Smileys get
   *    pointer-events:none so they don't steal the hover once over the button.
   * --------------------------------------------------------------------- */
  onReady(function () {
    var wrappers = document.querySelectorAll('.add_button_wrapper');

    for (var i = 0; i < wrappers.length; i++) {
      (function (wrapper) {
        var submitBtn = wrapper.querySelector(
          'input[type="submit"], .w-commerce-commerceaddtocartbutton'
        );
        var smileys = wrapper.querySelectorAll(
          '.smiley_01, .smiley_02, .smiley_03, .smiley_04, .smiley_05'
        );
        if (!submitBtn || smileys.length === 0) { return; }

        // Cache each smiley's REST scatter transform (set inline by the theme).
        // pointer-events:none so the (decorative) smileys, once gathered over the
        // button, don't steal hover and break the mouseenter/leave pair.
        var restT = [];
        for (var k = 0; k < smileys.length; k++) {
          restT.push(smileys[k].style.transform || smileys[k].style.webkitTransform || '');
          smileys[k].style.pointerEvents = 'none';
        }

        function gather() {
          if (prefersReducedMotion) { return; }
          for (var g = 0; g < smileys.length; g++) {
            var inT = restT[g]
              .replace(/translate3d\([^)]*\)/, 'translate3d(0px, 0px, 0px)')
              .replace(/scale3d\([^)]*\)/, 'scale3d(1, 1, 1)');
            smileys[g].style.transition =
              'transform 450ms cubic-bezier(0.34, 1.56, 0.64, 1), opacity 200ms ease';
            smileys[g].style.transform = inT;
            smileys[g].style.opacity = '1';
          }
        }
        function scatter() {
          for (var s = 0; s < smileys.length; s++) {
            smileys[s].style.transition = 'transform 350ms ease, opacity 350ms ease';
            smileys[s].style.opacity = '0';
            smileys[s].style.transform = restT[s];
          }
        }
        submitBtn.addEventListener('mouseenter', gather);
        submitBtn.addEventListener('mouseleave', scatter);
      }(wrappers[i]));
    }
  });

  /* -----------------------------------------------------------------------
   * D) SCROLL-LINKED reveals — ground-truth from live Webflow "scroll in view"
   *    (every value below measured on the live site via headless Chrome).
   *      Hero box (.box_hero, position:sticky): SHRINKS + TILTS on scroll —
   *        scale 1→0.80, rotateZ 0→10°, linear, reaching the end state at
   *        p = 1.61 viewports of scroll, then holding. Because the hero is
   *        sticky, that shrunk end state is what tucks it behind the footer at
   *        the page bottom (no peek). The photo pulse inside (.hp_slider) is a
   *        separate CSS animation and composes with this (parent × child).
   *      Footer (.box_footer): translateY 6%→0, scale 0.9→1, rotate -1°→0 as it
   *        enters. COMPLETES when its top reaches ~0.6·vh (≈0.4·vh of travel),
   *        well before the page bottom, then holds — so it fully covers the
   *        sticky hero. NO opacity change.
   *      Product cards (.box_product_card): slide up translateY 10%→0,
   *        COMPLETING when the card is 60% visible (snappier than live, per
   *        user request). NO opacity.
   *    GPU: 3D transforms + will-change (thtd-animations.css) keep the hero +
   *    footer on the compositor, so the per-frame rotate/scale doesn't repaint.
   * --------------------------------------------------------------------- */
  onReady(function () {
    if (prefersReducedMotion) { return; }
    var hero = document.querySelector('.box_hero');
    var footer = document.querySelector('.box_footer');
    var cards = Array.prototype.slice.call(
      document.querySelectorAll('.box_product_card')
    );
    if (!hero && !footer && cards.length === 0) { return; }

    var vh = window.innerHeight || document.documentElement.clientHeight;
    function scrollY() {
      return window.pageYOffset || document.documentElement.scrollTop || 0;
    }
    // Untransformed document-top (offsetTop chain ignores CSS transforms, so
    // it's feedback-free — unlike getBoundingClientRect on a transformed el).
    function docTop(el) {
      var y = 0;
      for (var n = el; n; n = n.offsetParent) { y += n.offsetTop; }
      return y;
    }
    var footerTopDoc = footer ? docTop(footer) : 0;
    // Per-card untransformed doc-top + height (cached; translateY % is relative
    // to the card's own height, and we derive "% visible" feedback-free).
    var cardInfo = [];
    function measureCards() {
      cardInfo = [];
      for (var c = 0; c < cards.length; c++) {
        cardInfo.push({ el: cards[c], top: docTop(cards[c]), h: cards[c].offsetHeight });
      }
    }
    measureCards();
    function remeasure() {
      vh = window.innerHeight || document.documentElement.clientHeight;
      if (footer) { footerTopDoc = docTop(footer); }
      measureCards();
    }

    var ticking = false;
    function update() {
      ticking = false;

      if (hero) {
        var ph = scrollY() / vh;
        if (ph > 1.61) { ph = 1.61; }           // end state: scale 0.80 / 10°
        var hs = (1 - 0.1243 * ph).toFixed(4);
        hero.style.transform =
          'translate3d(0,0,0) scale3d(' + hs + ',' + hs + ',1) ' +
          'rotateZ(' + (6.21 * ph).toFixed(3) + 'deg)';
      }

      if (footer) {
        var ftop = footerTopDoc - scrollY();      // untransformed viewport top
        var pf = (vh - ftop) / (0.4 * vh);
        if (pf < 0) { pf = 0; } else if (pf > 1) { pf = 1; }
        var fs = (0.9 + 0.1 * pf).toFixed(4);
        footer.style.transform =
          'translate3d(0,' + (6 * (1 - pf)).toFixed(3) + '%,0) ' +
          'scale3d(' + fs + ',' + fs + ',1) ' +
          'rotateZ(' + (-1 * (1 - pf)).toFixed(3) + 'deg)';
      }

      // PRODUCT CARDS — slide up: translateY 10%→0, COMPLETING when the card is
      // 60% visible. visibleFraction = (vh − untransformedTop)/cardHeight while
      // entering from the bottom; progress = visibleFraction / 0.6.
      for (var i = 0; i < cardInfo.length; i++) {
        var ci = cardInfo[i];
        var ctop = ci.top - scrollY();
        var pc = (vh - ctop) / (0.6 * ci.h);
        if (pc < 0) { pc = 0; } else if (pc > 1) { pc = 1; }
        ci.el.style.transform = 'translate3d(0,' + (10 * (1 - pc)).toFixed(3) + '%,0)';
      }
    }
    function onScroll() {
      if (!ticking) { ticking = true; window.requestAnimationFrame(update); }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () { remeasure(); onScroll(); }, { passive: true });
    window.addEventListener('load', function () { remeasure(); onScroll(); }, false);
    update();
  });

  /* -----------------------------------------------------------------------
   * E) 3D-CURSOR HOVER — INTENTIONALLY OMITTED.
   *    The pink ._3d_cursor icon (ic_3d_02_pink.svg) is opacity:0 in CSS. On the
   *    LIVE original it NEVER reveals on hover — verified 2026-06-04 with
   *    /tmp/thtd-introspect/live-cursor2.js: opacity stays 0 through
   *    enter / dwell / grab-drag / leave (the old Webflow IX2 reveal is dormant;
   *    the follower runs invisibly). The visible cursor on the 3D is the native
   *    CSS `cursor: grab` (rotate the model), which is unaffected by removing
   *    webflow.js. Per the user's explicit choice (2026-06-04: "nechat jako
   *    originál") we do NOT reveal the pink icon — leaving its default CSS state
   *    untouched keeps it hidden exactly like live. So: no JS here by design.
   *    (An earlier version DID reveal + follow it; that went BEYOND the original
   *    and was removed. To re-enable as an enhancement, restore from git/history.)
   * --------------------------------------------------------------------- */

  /* -----------------------------------------------------------------------
   * F) CONTACT HOVER BOXES — ground-truth from live (the box bg + icon bg are
   *    inline, set by the old IX2, so we reproduce in JS like the original did).
   *    On hover of .box_content.relative.no_underline.flex_right_bottom:
   *      box bg:  white  → green rgb(31,255,174)
   *      icon (.kontakt_icon_wrapper) bg: green → white, AND scale 1 → 1.12
   *    (no transform on the box, no shadow). ~300ms ease. Colours SWAP.
   * --------------------------------------------------------------------- */
  onReady(function () {
    var GREEN = 'rgb(31, 255, 174)';
    var WHITE = 'rgb(255, 255, 255)';
    var boxes = document.querySelectorAll(
      '.box_content.relative.no_underline.flex_right_bottom'
    );
    for (var i = 0; i < boxes.length; i++) {
      (function (box) {
        var icon = box.querySelector('.kontakt_icon_wrapper');
        if (prefersReducedMotion) {
          box.style.transition = 'none';
          if (icon) { icon.style.transition = 'none'; }
        } else {
          box.style.transition = 'background-color 300ms ease';
          if (icon) {
            icon.style.transition = 'background-color 300ms ease, transform 300ms ease';
          }
        }
        box.addEventListener('mouseenter', function () {
          box.style.backgroundColor = GREEN;
          if (icon) {
            icon.style.backgroundColor = WHITE;
            if (!prefersReducedMotion) { icon.style.transform = 'scale(1.12)'; }
          }
        });
        box.addEventListener('mouseleave', function () {
          box.style.backgroundColor = WHITE;
          if (icon) {
            icon.style.backgroundColor = GREEN;
            icon.style.transform = 'scale(1)';
          }
        });
      }(boxes[i]));
    }
  });

  /* -----------------------------------------------------------------------
   * G) LOAD-FADE SMILEYS
   *    Certain smiley images have inline opacity:0 and get stuck invisible
   *    when webflow.js is removed. We fade them in on DOMContentLoaded.
   *    Targets:
   *      - cart-main.liquid line ~50: .smiley.cart .smiley_image (no .down)
   *      - page-shopping-info.liquid line ~5: .smiley.cart .smiley_image.down
   *      - error-404.liquid line ~4: ._404_ikona (img with data-w-id)
   *    Note: .smiley_image.down has hover transition from thtd-animations.css —
   *    the load-fade just sets opacity:1 so hover works correctly at rest.
   * --------------------------------------------------------------------- */
  onReady(function () {
    // Cart and shopping-info smileys
    var cartSmileys = document.querySelectorAll(
      '.smiley.cart .smiley_image, .smiley.footer .smiley_image'
    );
    for (var i = 0; i < cartSmileys.length; i++) {
      (function (smiley) {
        var inlineOpacity = smiley.style.opacity;
        if (inlineOpacity === '0' || inlineOpacity === '0.0') {
          if (prefersReducedMotion) {
            smiley.style.opacity = '1';
          } else {
            smiley.style.transition = 'opacity 600ms ease';
            smiley.style.opacity = '0';
            smiley.offsetHeight; // reflow
            smiley.style.opacity = '1';
          }
        }
      }(cartSmileys[i]));
    }

    // 404 smiley
    var icon404 = document.querySelector('._404_ikona');
    if (icon404) {
      var inlineOpacity = icon404.style.opacity;
      if (inlineOpacity === '0' || inlineOpacity === '0.0') {
        if (prefersReducedMotion) {
          icon404.style.opacity = '1';
        } else {
          icon404.style.transition = 'opacity 600ms ease';
          icon404.style.opacity = '0';
          icon404.offsetHeight; // reflow
          icon404.style.opacity = '1';
        }
      }
    }
  });

  /* -----------------------------------------------------------------------
   * H) LOTTIE LOADERS — REMOVED (2026-06-12 perf audit). The lottie_light
   *    player ran the loader_02.json loop on the main thread for the whole
   *    page lifetime (11-23 s TBT on custom-jersey in Lighthouse). The
   *    loader is now a pure-CSS flipbook: snippets/thtd-loader.liquid +
   *    thtd-animations.css (same 8 icons, extracted from the Lottie JSON).
   * --------------------------------------------------------------------- */

  /* -----------------------------------------------------------------------
   * I) CUSTOM OPTIONS READINESS CHECKLIST (.cpo-checklist)
   *    Markup is rendered by snippets/custom-product-options.liquid. The
   *    logic must NOT live in an inline <script> inside the product form —
   *    udesly-shopify.min.js JSON.parses the FIRST <script> in the form as
   *    its variation config and an inline script there breaks add-to-cart
   *    ("Missing necessary data for Add to Cart!").
   * --------------------------------------------------------------------- */
  onReady(function () {
    var checkItems = document.querySelectorAll('[data-cpo-check]');
    if (!checkItems.length) { return; }
    var cpoForm = document.querySelector('[data-node-type="commerce-add-to-cart-form"]');
    if (!cpoForm) { return; }
    function cpoFilled(name) {
      var inputs = cpoForm.querySelectorAll('[name="properties[' + name + ']"]');
      if (!inputs.length) { return false; }
      if (inputs[0].type === 'radio') {
        for (var i = 0; i < inputs.length; i++) {
          if (inputs[i].checked) { return true; }
        }
        return false;
      }
      return inputs[0].value.trim() !== '';
    }
    function cpoRefresh() {
      for (var i = 0; i < checkItems.length; i++) {
        var ok = cpoFilled(checkItems[i].getAttribute('data-cpo-check'));
        checkItems[i].classList.toggle('is-ready', ok);
        checkItems[i].classList.toggle('is-missing', !ok);
      }
    }
    cpoForm.addEventListener('change', cpoRefresh);
    cpoForm.addEventListener('input', cpoRefresh);
    cpoRefresh();
  });

  /* -----------------------------------------------------------------------
   * J) FORM SCRIPT — window.Webflow.push replacement
   *    form-general-script.liquid (inline script, runs before this deferred
   *    script) pushes callbacks into window.Webflow[]. By the time this
   *    deferred module executes, those callbacks are already queued in the
   *    array. We drain the queue first, then replace .push so any future
   *    calls also fire immediately.
   * --------------------------------------------------------------------- */
  window.Webflow = window.Webflow || [];
  // Drain any callbacks already pushed by inline scripts
  var wfQueue = window.Webflow;
  if (Array.isArray(wfQueue)) {
    for (var qi = 0; qi < wfQueue.length; qi++) {
      if (typeof wfQueue[qi] === 'function') {
        try { wfQueue[qi](); } catch (e) { /* ignore */ }
      }
    }
  }
  // Replace .push so future calls fire immediately
  window.Webflow.push = function (fn) {
    if (typeof fn === 'function') {
      try { fn(); } catch (e) { /* ignore */ }
    }
  };

  /* -----------------------------------------------------------------------
   * K) FAQ ACCORDION (sections/faq.liquid)
   *    Click a .faq_q button → toggle its .faq_a panel via max-height (CSS does
   *    the transition) + aria-expanded + .is-open (drives the +/− icon). Panels
   *    open independently. Re-measure open panels on resize so a reflow (e.g.
   *    rotation) never clips a long answer.
   * --------------------------------------------------------------------- */
  onReady(function () {
    var items = document.querySelectorAll('.faq_item');
    if (!items.length) { return; }
    for (var i = 0; i < items.length; i++) {
      (function (item) {
        var btn = item.querySelector('.faq_q');
        var panel = item.querySelector('.faq_a');
        if (!btn || !panel) { return; }
        btn.addEventListener('click', function () {
          var isOpen = btn.getAttribute('aria-expanded') === 'true';
          if (isOpen) {
            panel.style.maxHeight = '';
            btn.setAttribute('aria-expanded', 'false');
            item.classList.remove('is-open');
          } else {
            panel.style.maxHeight = panel.scrollHeight + 'px';
            btn.setAttribute('aria-expanded', 'true');
            item.classList.add('is-open');
          }
        });
      }(items[i]));
    }
    window.addEventListener('resize', function () {
      for (var j = 0; j < items.length; j++) {
        var b = items[j].querySelector('.faq_q');
        var p = items[j].querySelector('.faq_a');
        if (b && p && b.getAttribute('aria-expanded') === 'true') {
          p.style.maxHeight = p.scrollHeight + 'px';
        }
      }
    });
  });

}());
